import { Button, Card, Descriptions, Table, Tag, message, Divider } from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import { ArrowLeftOutlined } from "@ant-design/icons";
import LoadingBox from "../../Components/LoadingBox";
import { API } from "../../Config";
import { GET } from "../../Config/ApiRequests";

const ContraViewPage = ({ contraId, onBack }) => {
  const [loading, setLoading] = useState(true);
  const [contraData, setContraData] = useState(null);

  useEffect(() => {
    if (contraId) {
      fetchContraDetails();
    }
  }, [contraId]);

  const fetchContraDetails = async () => {
    setLoading(true);
    try {
      const response = await GET(`${API.CONTRA_VOUCHER_DETAIL}${contraId}`);
      const data = response?.data || response;
      if (data?.id) {
        setContraData(data);
      } else {
        message.error("Failed to load contra details");
      }
    } catch (error) {
      message.error("Failed to load contra details");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Ledger Transaction columns
  const ledgerColumns = [
    {
      title: "#",
      key: "index",
      width: 10,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Date",
      dataIndex: "trans_date",
      key: "trans_date",
      width: "12%",
      render: (v) => (v ? moment(v).format("DD-MM-YYYY") : "-"),
    },
    {
      title: "Trans Type",
      dataIndex: "trans_type",
      key: "trans_type",
      width: "10%",
      render: (v) => v || "-",
    },
    {
      title: "Account",
      key: "account",
      width: "20%",
      render: (_, record) => {
        const accNo = record?.account_no || "-";
        const accName = record?.accounts?.account_name;
        return accName ? `${accNo} - ${accName}` : accNo;
      },
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: "12%",
      align: "right",
      render: (v) => (v != null ? Number(v).toFixed(2) : "-"),
    },
    {
      title: "Dr/Cr",
      dataIndex: "dr_or_cr",
      key: "dr_or_cr",
      width: "8%",
      align: "center",
      render: (v) => (
        <Tag color={v === "Dr" ? "green" : "red"}>{v || "-"}</Tag>
      ),
    },
    {
      title: "Note",
      dataIndex: "note",
      key: "note",
      width: "30%",
      ellipsis: true,
      render: (text) => text || "-",
    },
  ];

  if (loading) {
    return <LoadingBox />;
  }

  if (!contraData) {
    return (
      <Card>
        <p>Contra not found</p>
        <Button onClick={onBack}>Back to Contra List</Button>
      </Card>
    );
  }

  return (
    <Card
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
            Back
          </Button>
          <span>Contra Voucher Details</span>
          <Tag color="purple">CV</Tag>
        </div>
      }
    >
      {/* Basic Info */}
      <Descriptions bordered column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1 }}>
        <Descriptions.Item label="Contra ID">{contraData?.id || "-"}</Descriptions.Item>
        <Descriptions.Item label="Date">
          {contraData?.voucher_date ? moment(contraData?.voucher_date).format("DD-MM-YYYY") : "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Ledger From">
          {contraData?.fromAccount
            ? `${contraData.ledger_from} - ${contraData.fromAccount.account_name}`
            : contraData?.ledger_from || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Ledger To">
          {contraData?.toAccount
            ? `${contraData.ledger_to} - ${contraData.toAccount.account_name}`
            : contraData?.ledger_to || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Amount">
          <strong style={{ color: "#1890ff" }}>
            {contraData?.amount != null ? Number(contraData?.amount).toFixed(2) : "-"}
          </strong>
        </Descriptions.Item>
        <Descriptions.Item label="Description">
          {contraData?.description || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Created At">
          {contraData?.created_at
            ? moment(contraData?.created_at).format("DD-MM-YYYY HH:mm")
            : "-"}
        </Descriptions.Item>
      </Descriptions>

      {/* Ledger Transactions */}
      {contraData?.ledgerTransactions?.length > 0 && (
        <>
          <Divider />
          <div style={{ marginTop: 24 }}>
            <h3>Ledger Transactions</h3>
            <Table
              columns={ledgerColumns}
              dataSource={contraData?.ledgerTransactions || []}
              pagination={false}
              size="small"
              rowKey="id"
              locale={{ emptyText: "No ledger transactions" }}
            />
          </div>
        </>
      )}
    </Card>
  );
};

export default ContraViewPage;
